/**
 * Text — MD3 typography role component for the Mango — Farmer Market kit.
 *
 * Exposes the full MD3 type scale (display / headline / title / body / label,
 * each large/medium/small) as a single `variant` prop, applying fontSize,
 * lineHeight and fontWeight DIRECTLY from the `typography` token rather than
 * relying on MUI theme variants. This keeps the kit decoupled from whatever
 * the active MUI theme happens to define and guarantees the rural-bumped
 * scale from `theme/tokens.ts` is honoured verbatim.
 *
 * Family rule (mirrors `colors_and_type.css` .dm-* classes):
 *   - display / headline / title → Bricolage Grotesque (fontFamilyDisplay)
 *   - body / label               → Noto Sans (fontFamily)
 *
 * Renders through MUI `Typography` so it slots into MUI layouts, but owns its
 * own prop surface — `sx` is NOT exposed publicly (swap-promise).
 */
import { Typography } from '@mui/material';
import { forwardRef, type ElementType, type ReactNode } from 'react';

import { typography } from '../theme/tokens.js';

export type TextVariant =
  | 'display-large'
  | 'display-medium'
  | 'display-small'
  | 'headline-large'
  | 'headline-medium'
  | 'headline-small'
  | 'title-large'
  | 'title-medium'
  | 'title-small'
  | 'body-large'
  | 'body-medium'
  | 'body-small'
  | 'label-large'
  | 'label-medium'
  | 'label-small';

export interface TextProps {
  /** MD3 type-scale role. Default `body-large` (the kit's default body). */
  variant?: TextVariant;
  /** Content. */
  children?: ReactNode;
  /** Text colour. Any CSS colour string; defaults to inherited `currentColor`. */
  color?: string;
  /** Horizontal alignment. */
  align?: 'left' | 'center' | 'right' | 'justify';
  /** Semantic element to render (e.g. 'h1', 'p', 'span'). Defaults per variant. */
  as?: ElementType;
  /** Single-line ellipsis truncation. */
  truncate?: boolean;
  /** Pass-through className for layout composition. */
  className?: string;
  /** Accessible id (e.g. to label a region). */
  id?: string;
}

/** Keys of `typography` that hold a {fontSize,lineHeight,fontWeight} scale. */
type ScaleKey =
  | 'displayLarge'
  | 'displayMedium'
  | 'displaySmall'
  | 'headlineLarge'
  | 'headlineMedium'
  | 'headlineSmall'
  | 'titleLarge'
  | 'titleMedium'
  | 'titleSmall'
  | 'bodyLarge'
  | 'bodyMedium'
  | 'bodySmall'
  | 'labelLarge'
  | 'labelMedium'
  | 'labelSmall';

/** Maps each variant to its token key + default semantic element. */
const VARIANT_MAP: Record<
  TextVariant,
  { token: ScaleKey; family: 'display' | 'body'; as: ElementType }
> = {
  'display-large': { token: 'displayLarge', family: 'display', as: 'h1' },
  'display-medium': { token: 'displayMedium', family: 'display', as: 'h1' },
  'display-small': { token: 'displaySmall', family: 'display', as: 'h2' },
  'headline-large': { token: 'headlineLarge', family: 'display', as: 'h2' },
  'headline-medium': { token: 'headlineMedium', family: 'display', as: 'h3' },
  'headline-small': { token: 'headlineSmall', family: 'display', as: 'h3' },
  'title-large': { token: 'titleLarge', family: 'display', as: 'h4' },
  'title-medium': { token: 'titleMedium', family: 'display', as: 'h5' },
  'title-small': { token: 'titleSmall', family: 'display', as: 'h6' },
  'body-large': { token: 'bodyLarge', family: 'body', as: 'p' },
  'body-medium': { token: 'bodyMedium', family: 'body', as: 'p' },
  'body-small': { token: 'bodySmall', family: 'body', as: 'p' },
  'label-large': { token: 'labelLarge', family: 'body', as: 'span' },
  'label-medium': { token: 'labelMedium', family: 'body', as: 'span' },
  'label-small': { token: 'labelSmall', family: 'body', as: 'span' },
};

export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  { variant = 'body-large', children, color, align, as, truncate, className, id },
  ref,
) {
  const { token, family, as: defaultAs } = VARIANT_MAP[variant];
  const scale = typography[token];
  const fontFamily =
    family === 'display' ? typography.fontFamilyDisplay : typography.fontFamily;

  return (
    <Typography
      ref={ref}
      component={as ?? defaultAs}
      id={id}
      className={className}
      align={align}
      sx={{
        fontFamily,
        fontSize: scale.fontSize,
        lineHeight: scale.lineHeight,
        fontWeight: scale.fontWeight,
        color: color ?? 'inherit',
        margin: 0,
        ...(truncate
          ? {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }
          : null),
      }}
    >
      {children}
    </Typography>
  );
});
